<template>
  <div
    class="overflow-y-auto border-t-2 border-black px-9 pb-24 pt-2 transition sm:pb-8 sm:pt-6"
    :class="[isOpen ? 'mt-10 sm:mt-0' : 'pt-2']"
  >
    <div
      class="flex cursor-pointer items-center justify-between p-2 pr-4"
      data-testid="questionaire-collabsible"
      @click="toggle"
    >
      <div class="flex items-center gap-2">
        <LazyIconFielmannChevronUp
          class="size-6 transition"
          :class="{ '-rotate-180': isOpen }"
        />
        <Headline tag="span" look="h4">
          {{ $t('contact_lens_consultant.collabsible.title') }}
        </Headline>
      </div>
    </div>
    <div
      class="fixed bottom-0 left-1/2 flex w-full -translate-x-1/2 animate-appear-delayed flex-col items-stretch bg-white px-4 pb-6 pt-2 opacity-0 sm:w-auto sm:bg-transparent"
      data-testid="cta-container"
    >
      <slot name="cta">
        <FimButton type="primary">
          {{ $t('contact_lens_consultant.collabsible.cta') }}
        </FimButton>
      </slot>
    </div>
    <div
      class="transition-height"
      :class="!isOpen && 'overflow-hidden'"
      :style="isOpen ? { height: contentHeight } : { height: 0 }"
      data-testid="collapsible-content"
    >
      <div
        ref="content"
        class="flex flex-col gap-y-2 pb-2 md:pb-14 lg:flex-row lg:justify-between"
      >
        <div class="flex flex-col gap-y-1">
          <div
            v-for="(item, key) in details"
            :key="`item-${key}`"
            :data-testid="`detail-item-${key}`"
            class="flex flex-row flex-wrap gap-x-2 py-1 pl-10 pr-4 text-lg lg:flex-nowrap"
          >
            <div
              class="flex w-full justify-between font-medium lg:w-auto lg:font-normal"
              :data-testid="`question-${key}`"
            >
              <div v-if="typeof item.question === 'string'">
                {{ item.question }}
              </div>
              <div v-else-if="item?.question?.type === 'doc'">
                <RichTextRenderer :document="item.question" />
              </div>
            </div>
            <div
              class="slg:flex-row flex flex-col gap-px lg:w-auto"
              :data-testid="`answers-${key}`"
            >
              <div
                v-for="(answer, answerKey) in item.answers"
                :key="`item-${answerKey}`"
                class="flex justify-between"
              >
                <div v-if="typeof answer === 'string'">
                  {{ answer }}
                </div>
                <div v-else-if="answer?.type === 'doc'">
                  <RichTextRenderer :document="answer" />
                </div>
                <span
                  v-if="answerKey < (item.answers?.length ?? 1) - 1"
                  class="hidden lg:inline"
                  >,
                </span>
              </div>
              <div v-if="!item.answers?.length">-</div>
            </div>
            <button
              class=""
              type="button"
              :aria-label="`Edit answert for question ${key + 1}`"
              :data-testid="`edit-button-${key}`"
              @click="() => edit(key)"
            >
              <LazyIconFielmannPencil class="size-4 text-[#0140E3]" />
            </button>
          </div>
        </div>
        <div
          class="ml-10 flex flex-col items-start gap-2 lg:-mt-10 lg:ml-0"
          data-testid="action-container"
        >
          <slot name="action"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core'
import type {
  SbQuestionairePayload,
  SbQuestionnaireStep,
} from '~/storyblok/types/storyblok'

type Props = {
  payload: SbQuestionairePayload
  steps: SbQuestionnaireStep[]
}

const props = withDefaults(defineProps<Props>(), {})

const details = computed(() => {
  return props.steps.map((step) => {
    const payloadValue = props.payload[step.question_id]

    return {
      question: step.question,
      answers: step.answers
        ?.filter((answer) => {
          if (typeof payloadValue === 'string') {
            return answer.answer_id === payloadValue
          }

          return !!payloadValue?.[answer.answer_id]
        })
        .map((answer) => answer.answer),
    }
  })
})

/* toggle slide effect */
const emit = defineEmits<{
  (e: 'click:restart'): void
  (e: 'click:toggle', params: boolean): void
  (e: 'click:edit', params: number): void
}>()

const content = useTemplateRef('content')
const isOpen = ref(false)

const toggle = () => {
  isOpen.value = !isOpen.value
  emit('click:toggle', isOpen.value)
}

const edit = (i: number) => {
  emit('click:edit', i)
}

const { height } = useElementSize(
  content,
  { width: 0, height: 0 },
  { box: 'border-box' },
)
const contentHeight = computed(() => `${height.value || 0}px`)
</script>
